'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { MessageSquare } from 'lucide-react';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
  NoticeBanner,
  getNoticeBannerActionClasses,
} from '@/shared/ui';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { SettingsSectionRow } from './SettingsSection';

interface PromptsSectionProps {
  conceptPrompt: string;
  anglePrompt: string;
  onConceptPromptChange: (value: string) => void;
  onAnglePromptChange: (value: string) => void;
  hasChanges?: boolean;
  isDerivative?: boolean;
  isRefine?: boolean;
  /** True when the Explore-main brief should nudge the user to fill in
   *  project goals first (`!!projectId && !hasProjectGoals`). Drives the
   *  info banner inside the drill-down sheet. */
  goalsRequired?: boolean;
  /** Project id — present only on the Explore-main path. */
  projectId?: string;
  /** Opens the sibling Project goals sheet. The banner link calls this after
   *  closing the prompts sheet so the two sheets never stack. */
  onOpenGoals?: () => void;
}

/**
 * Brief prompt editor. Renders ONE prompt at a time based on mode:
 *   - Refine          → angle prompt (delta from parent render), inline
 *   - Explore deriv.  → angle prompt (per-angle likes), NestedSheet drill-down
 *   - Explore main    → concept prompt (concept-wide stylistic likes), in a
 *                       NestedSheet drill-down so the brief group reads as one
 *                       row alongside Project goals / Sketches
 *
 * Both Explore paths (main AND derivative) use the drill-down sheet so the
 * brief reads as a compact row alongside its siblings; only Refine keeps the
 * inline label+editor. The goals-empty info banner (whose inline "project
 * tasks" link jumps to the Project goals editor) is a MAIN-angle affordance —
 * derivatives inherit the brief from the main render and expose no goals UI.
 */
export function PromptsSection({
  conceptPrompt,
  anglePrompt,
  onConceptPromptChange,
  onAnglePromptChange,
  hasChanges = false,
  isDerivative = false,
  isRefine = false,
  goalsRequired = false,
  projectId,
  onOpenGoals,
}: PromptsSectionProps) {
  const t = useTranslations('render.settings.prompts');
  // Reuse the established "Auto" string (render.settings.style.auto) for the
  // empty collapsed-row value rather than minting a duplicate.
  const tStyle = useTranslations('render.settings.style');
  const [sheetOpen, setSheetOpen] = useState(false);

  const isExplore = !isRefine;

  // Explore (main AND derivative): drill-down sheet row. Main edits the
  // concept-wide prompt; a derivative edits its per-angle prompt. Subtitle
  // shows the entered text (the SettingsSectionRow truncates it) or the shared
  // "Auto" string when empty.
  if (isExplore) {
    const prompt = isDerivative ? anglePrompt : conceptPrompt;
    const onPromptChange = isDerivative
      ? onAnglePromptChange
      : onConceptPromptChange;
    // Per-angle prompts stay short (delta-sized); the concept-wide brief is
    // roomier. Preserves the limits each field had before this unification.
    const maxLength = isDerivative ? 200 : 600;
    const trimmedPrompt = prompt.trim();
    return (
      <>
        <SettingsSectionRow
          icon={MessageSquare}
          title={t('title')}
          subtitle={trimmedPrompt || tStyle('auto')}
          hasChanges={hasChanges}
          onClick={() => setSheetOpen(true)}
        />

        <NestedSheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <NestedSheetContent>
            <NestedSheetHeader>
              <NestedSheetTitle>{t('title')}</NestedSheetTitle>
            </NestedSheetHeader>

            <NestedSheetBody className="px-4">
              <div className="space-y-4 py-2">
                {/* Goals-empty nudge — main angle only. The inline "project
                    tasks" link closes this sheet then opens the Project goals
                    editor so the two sheets never stack. When the nudge is not
                    shown, a plain description explains the field instead. */}
                {!isDerivative && goalsRequired && projectId && onOpenGoals ? (
                  <NoticeBanner
                    variant="info"
                    title={t.rich('goalsBannerTitle', {
                      link: (chunks) => (
                        <button
                          type="button"
                          className={`underline underline-offset-2 ${getNoticeBannerActionClasses('info')}`}
                          onClick={() => {
                            setSheetOpen(false);
                            onOpenGoals();
                          }}
                        >
                          {chunks}
                        </button>
                      ),
                    })}
                    message={t('goalsBannerMessage')}
                  />
                ) : (
                  <p className="text-form-help text-muted-foreground">
                    {t('likesDescription')}
                  </p>
                )}

                <Textarea
                  value={prompt}
                  onChange={(e) => onPromptChange(e.target.value)}
                  placeholder={t('likesPlaceholder')}
                  className="min-h-[400px] resize-none bg-white dark:bg-black"
                  maxLength={maxLength}
                  // Clarity opt-in: the brief prompt is user-authored design
                  // intent, not PII — unmask it in session replays (Clarity
                  // masks all inputs by default).
                  data-clarity-unmask="true"
                />
              </div>
            </NestedSheetBody>
          </NestedSheetContent>
        </NestedSheet>
      </>
    );
  }

  // Refine: inline label + editor. Edits the angle prompt as a delta from the
  // parent render.
  return (
    <div className="px-4 pt-3 pb-3 space-y-2">
      {/* Label row (icon + title) — no chevron, not clickable; the editor
          below it owns interaction. */}
      <div className="flex items-center gap-3">
        <MessageSquare
          className="h-5 w-5 text-primary flex-shrink-0"
          strokeWidth={1.5}
        />
        <span className="text-card-body text-foreground">
          {t('refineTitle')}
        </span>
        {hasChanges && (
          <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
        )}
      </div>

      <Textarea
        value={anglePrompt}
        onChange={(e) => onAnglePromptChange(e.target.value)}
        placeholder={t('refinePlaceholder')}
        className="min-h-[64px] resize-none bg-white dark:bg-black"
        maxLength={200}
        // Clarity opt-in: the refine brief is user-authored design intent, not
        // PII — unmask it in session replays.
        data-clarity-unmask="true"
      />
    </div>
  );
}
